// src/components/CountryContentClient.tsx
"use client";

import {
  Cpu,
  Gauge,
  Home,
  Laptop,
  Move,
  PlaneTakeoff,
  Zap,
} from "lucide-react";
import Image from "next/image";
import React, { useMemo, useRef } from "react";

import Card from "@/components/ui/Card";
import ComparisonRow from "@/components/ui/ComparisonRow";
import DataPlanCard from "@/components/ui/DataPlanCard";
import RightForYouItem from "@/components/ui/RightForYouItem";
import StepItem from "@/components/ui/StepItem";
import { useTranslations } from "@/context/TranslationsContext";
import { useAutoplayOnVisible } from "@/hooks/useAutoplayOnVisible";

interface CountryContentClientProps {
  countryName: string;
  networks: string[];
}

const CountryContentClient: React.FC<CountryContentClientProps> = React.memo(
  ({ countryName, networks }) => {
    const { t, direction } = useTranslations();
    const isRTL = direction === "rtl";
    const videoRef = useRef<HTMLVideoElement>(null);

    useAutoplayOnVisible(videoRef);

    const networksList = useMemo(() => networks.join(", "), [networks]);

    const dataPlans = useMemo(
      () => [
        {
          title: t("countryPage.dataPlans.plan1Title"),
          description: t("countryPage.dataPlans.plan1Description"),
        },
        {
          title: t("countryPage.dataPlans.plan2Title"),
          description: t("countryPage.dataPlans.plan2Description"),
        },
        {
          title: t("countryPage.dataPlans.plan3Title"),
          description: t("countryPage.dataPlans.plan3Description"),
        },
      ],
      [t]
    );

    const features = useMemo(
      () => [
        {
          icon: <Zap className="h-8 w-8 text-white" />,
          title: t("countryPage.whyChoose.instantActivationHeading", {
            country: countryName,
          }),
          description: t("countryPage.whyChoose.instantActivationDetail"),
        },
        {
          icon: <Move className="h-8 w-8 text-white" />,
          title: t("countryPage.whyChoose.flexiblePlansHeading"),
          description: t("countryPage.whyChoose.flexiblePlansDetail"),
        },
        {
          icon: <Gauge className="h-8 w-8 text-white" />,
          title: t("countryPage.whyChoose.noThrottlingHeading"),
          description: t("countryPage.whyChoose.noThrottlingDetail"),
        },
      ],
      [t, countryName]
    );

    const steps = useMemo(
      () => [
        {
          number: 1,
          title: t("countryPage.howToGetConnected.step1Title"),
          description: t("countryPage.howToGetConnected.step1Description", {
            country: countryName,
          }),
        },
        {
          number: 2,
          title: t("countryPage.howToGetConnected.step2Title"),
          description: t("countryPage.howToGetConnected.step2Description"),
        },
        {
          number: 3,
          title: t("countryPage.howToGetConnected.step3Title"),
          description: t("countryPage.howToGetConnected.step3Description", {
            country: countryName,
          }),
        },
      ],
      [t, countryName]
    );

    const comparisonRows = useMemo(
      () => [
        {
          feature: t("countryPage.comparison.physicalSim"),
          traditionalSim: t("countryPage.comparison.traditionalSimYes"),
          alodataEsim: t("countryPage.comparison.alodataEsimNo"),
        },
        {
          feature: t("countryPage.comparison.activationTime"),
          traditionalSim: t("countryPage.comparison.traditionalSimHours"),
          alodataEsim: t("countryPage.comparison.alodataEsimInstant"),
        },
        {
          feature: t("countryPage.comparison.networksInCountry", {
            country: countryName,
          }),
          traditionalSim: t("countryPage.comparison.traditionalSimLimited"),
          alodataEsim: t("countryPage.comparison.alodataEsimNetworks", {
            networks: networksList,
          }),
        },
        {
          feature: t("countryPage.comparison.flexibility"),
          traditionalSim: t("countryPage.comparison.traditionalSimLow"),
          alodataEsim: t("countryPage.comparison.alodataEsimHigh"),
        },
        {
          feature: t("countryPage.comparison.cost"),
          traditionalSim: t("countryPage.comparison.traditionalSimHigh"),
          alodataEsim: t("countryPage.comparison.alodataEsimAffordable"),
        },
      ],
      [t, countryName, networksList]
    );

    const rightsForYou = useMemo(
      () => [
        {
          icon: <PlaneTakeoff className="h-8 w-8 text-white" />,
          description: t("countryPage.isRightForYou.travelers", {
            country: countryName,
          }),
        },
        {
          icon: <Laptop className="h-8 w-8 text-white" />,
          description: t("countryPage.isRightForYou.remoteWorkers", {
            country: countryName,
          }),
        },
        {
          icon: <Home className="h-8 w-8 text-white" />,
          description: t("countryPage.isRightForYou.locals", {
            country: countryName,
          }),
        },
      ],
      [t, countryName]
    );

    return (
      <section
        className="mt-16 bg-gray-50 py-12 md:py-16 lg:py-20 overflow-visible"
        dir={isRTL ? "rtl" : "ltr"}
      >
        <div className="container mx-auto px-4">
          <div className="mx-auto w-full max-w-screen-lg my-8">
            <div className="relative w-full max-w-screen-lg overflow-hidden aspect-video rounded-lg shadow-md">
              <video
                ref={videoRef}
                src="/video/android.mp4"
                className="absolute inset-0 w-full h-full object-cover"
                muted
                loop
                playsInline
                preload="none"
              />
            </div>
          </div>

          <div className="mb-16 text-center">
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4 bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent leading-normal py-4">
              {t("countryPage.stayConnected", { country: countryName })}
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              {t("countryPage.intro", { country: countryName })}
            </p>
          </div>

          <div className="border-b border-gray-200 mb-16" />

          <div className="mb-16 flex flex-col md:flex-row items-center overflow-visible">
            <div className="md:w-1/2 mb-8 md:mb-0 flex justify-center">
              <div className="flex items-center justify-center bg-gradient-to-r from-teal-400 to-blue-500 rounded-full p-8">
                <Cpu className="w-32 h-32 md:w-40 md:h-40 text-white" />
              </div>
            </div>
            <div className="md:w-1/2 md:pl-12">
              <h3 className="text-3xl md:text-4xl font-semibold mb-4 text-gray-800 leading-snug py-2">
                {t("countryPage.whatIsEsim")}
              </h3>
              <p className="text-lg md:text-xl text-gray-700">
                {t("countryPage.whatIsEsimDescription", {
                  country: countryName,
                })}
              </p>
            </div>
          </div>

          <div className="mb-16">
            <h3 className="text-3xl md:text-4xl font-semibold mb-8 bg-gradient-to-r from-pink-500 to-red-500 bg-clip-text text-transparent text-center leading-snug py-2">
              {t("countryPage.whyChoose", { country: countryName })}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((f, i) => (
                <Card key={i} {...f} />
              ))}
            </div>
          </div>

          <div className="border-b border-gray-200 mb-16" />

          <div className="mb-16">
            <h3 className="text-3xl md:text-4xl font-semibold mb-8 bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent text-center leading-snug py-2">
              {t("countryPage.howToGetConnected", { country: countryName })}
            </h3>
            <ol className="space-y-8">
              {steps.map((s) => (
                <StepItem key={s.number} {...s} />
              ))}
            </ol>
          </div>

          <div className="border-b border-gray-200 mb-16" />

          <div className="mb-16">
            <h3 className="text-3xl md:text-4xl font-semibold mb-8 bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent text-center leading-snug py-2">
              {t("countryPage.dataPlans", { country: countryName })}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {dataPlans.map((p, i) => (
                <DataPlanCard key={i} {...p} />
              ))}
            </div>
          </div>

          <div className="border-b border-gray-200 mb-16" />

          <div className="mb-16">
            <h3 className="text-3xl md:text-4xl font-semibold mb-8 bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent text-center leading-snug py-2">
              {t("countryPage.comparison.title", { country: countryName })}
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border-collapse rounded-lg shadow-lg">
                <thead>
                  <tr>
                    <th className="py-3 px-6 bg-gradient-to-r from-indigo-500 to-purple-600 text-left text-xs md:text-sm font-medium text-white uppercase tracking-wider">
                      {t("countryPage.comparison.feature")}
                    </th>
                    <th className="py-3 px-6 bg-gradient-to-r from-indigo-500 to-purple-600 text-left text-xs md:text-sm font-medium text-white uppercase tracking-wider">
                      {t("countryPage.comparison.traditionalSim")}
                    </th>
                    <th className="py-3 px-6 bg-gradient-to-r from-indigo-500 to-purple-600 text-left text-xs md:text-sm font-medium text-white uppercase tracking-wider">
                      {t("countryPage.comparison.alodataEsim")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((r, i) => (
                    <ComparisonRow key={i} {...r} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="border-b border-gray-200 mb-16" />

          <div className="mb-16">
            <h3 className="text-3xl md:text-4xl font-semibold mb-8 bg-gradient-to-r from-red-500 to-yellow-500 bg-clip-text text-transparent text-center leading-snug py-2">
              {t("countryPage.isRightForYou", { country: countryName })}
            </h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {rightsForYou.map((r, i) => (
                <RightForYouItem key={i} {...r} />
              ))}
            </ul>
          </div>

          <div className="border-b border-gray-200 mb-16" />

          <div className="mb-16 text-center">
            <h3 className="text-3xl md:text-4xl font-semibold mb-4 bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent leading-snug py-2">
              {t("countryPage.trustedByMillions")}
            </h3>
            <p className="text-lg md:text-xl text-gray-700 max-w-2xl mx-auto mb-8">
              {t("countryPage.trustedByMillionsDescription", {
                country: countryName,
              })}
            </p>
            <div className="mt-8 flex justify-center space-x-4 md:space-x-8">
              <a
                href="https://www.trustpilot.com/review/alodata.net"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Trustpilot Reviews"
              >
                <Image
                  src="/images/trustpilot.png"
                  alt="Trustpilot"
                  width={192}
                  height={48}
                  loading="lazy"
                  style={{ width: "auto", height: "3rem" }}
                  className="mx-auto"
                />
              </a>
            </div>
          </div>
        </div>
      </section>
    );
  }
);

export default CountryContentClient;
